// When adding typed mutations, read ./README.md first — it covers the
// backend-schema → Params/RetVal translation and the expected migration pattern.
import type {ModelOverview} from "../../cdx-models";
import type {MakeMeta} from "../../cdx-models/utils/MakeModel";
import type {Invalidate} from "../mate/mate-shared";
import type {CreatedIdsFn, MutationDesc, ModelName, Tx} from "../mate/mate-types";

// Mirrors `CoverFileData` from `node-api/src/context/files/File.ts` — the
// payload accepted by every action that takes `coverFileData` /
// `profileImageData` (users.update, decks.update, projects.updateCover,
// cards.create, …). Lives here so api.ts and feature code can share it
// without coupling the API layer to a feature directory.
export type CoverFileData =
  | {url: string; fileName?: string; noAccount?: boolean}
  | {url: string; external: true};

// Pull the Field/BelongsTo/HasMany generic slots back out of a model class
// via its $meta. Mirrors the inference pattern used in `MakeModel`'s
// `WithLoadedFields`.
type FieldsOf<MN extends ModelName> = ModelOverview[MN] extends {
  $meta: MakeMeta<any, infer F, any, any>;
}
  ? F
  : never;
type BelongsToOf<MN extends ModelName> = ModelOverview[MN] extends {
  $meta: MakeMeta<any, any, infer B, any>;
}
  ? B
  : never;
type HasManyOf<MN extends ModelName> = ModelOverview[MN] extends {
  $meta: MakeMeta<any, any, any, infer H>;
}
  ? H
  : never;

// Field values accept `Invalidate` (the `INVALIDATE` sentinel) and `null` in
// addition to the field's natural type:
// - `Invalidate` is the explicit "I don't know the new value" signal.
// - `null` is accepted because mutation params often carry `T | null` even when
//   the model field is non-null; the cache treats `null` as invalidate-only and
//   doesn't write it through (`invalidation-helpers.ts` skips assignment for
//   `null`/`INVALIDATE`). The goal of the per-model typing here is key/typo
//   catching, not enforcing model nullability at the cache write layer.
type PlainFields<MN extends ModelName> = {
  [K in keyof FieldsOf<MN>]?: FieldsOf<MN>[K] | null | Invalidate;
};
type BelongsToFields<MN extends ModelName> = {
  [K in keyof BelongsToOf<MN>]?: BelongsToOf<MN>[K]["model"] | null | Invalidate;
};
type BelongsToFkFields<MN extends ModelName> = {
  [K in keyof BelongsToOf<MN> as BelongsToOf<MN>[K]["fk"]]?:
    | BelongsToOf<MN>[K]["id"]
    | null
    | Invalidate;
};
type HasManyFields<MN extends ModelName> = {
  [K in keyof HasManyOf<MN>]?: unknown[] | null | Invalidate;
};

// `cache.ts` strips `id`/`ids` off the destructure first, so they're orthogonal
// to per-model fields.
type IdAnchors = {id?: string; ids?: string[]};

export type ImplicitData<MN extends ModelName> = IdAnchors &
  PlainFields<MN> &
  BelongsToFields<MN> &
  BelongsToFkFields<MN> &
  HasManyFields<MN>;

type DescBase = Pick<
  MutationDesc,
  "convertDataForOptimistic" | "implicit" | "optimistic" | "createdIds"
>;

// Discriminated by `desc.type` + `desc.model` so that `data` autocompletes
// per-model fields/fks/relations. `cache.ts` widens to `MutationDesc` at the
// boundary, so the producer side can stay tight.
//
// Helpers below (`implCreate`/`implUpdate`/`implDelete`/...) take the model as a
// `<const M extends ModelName>` argument and infer `data: ImplicitData<M>` directly,
// which gives true per-model autocomplete and typo catching at call sites. Inline
// literals here still satisfy the discriminated union for assignability checks but
// no longer need the old fallback branch — every site goes through a helper.
export type ImplicitUpdate =
  | {
      [MN in ModelName]: {
        desc: DescBase & {type: "create" | "update" | "delete"; model: MN};
        data: ImplicitData<MN>;
      };
    }[ModelName]
  | {
      desc: DescBase & {type: "void"};
      data: Record<string, any>;
    };

// Per-model helpers — preferred over inline `{desc, data}` literals because
// `<const M>` infers the literal model name and narrows `data` to that model's
// fields, giving real autocomplete and typo catching.
//
// The return type is a single `ImplicitUpdate` cast: TS can't unify a generic
// `model: M` against the discriminated-union branches, but each helper's
// invocation site has been narrowed by `<const M>` already, so the cast is
// safe.
export const implCreate = <const M extends ModelName>(
  model: M,
  data: ImplicitData<M> = {} as ImplicitData<M>
): ImplicitUpdate => ({desc: {type: "create", model}, data}) as ImplicitUpdate;

export const implUpdate = <const M extends ModelName>(
  model: M,
  data: ImplicitData<M>
): ImplicitUpdate => ({desc: {type: "update", model}, data}) as ImplicitUpdate;

export const implDelete = <const M extends ModelName>(
  model: M,
  data: ImplicitData<M> = {} as ImplicitData<M>
): ImplicitUpdate => ({desc: {type: "delete", model}, data}) as ImplicitUpdate;

/** Shows a change up front that no plain `update`/`delete` describes. */
export const implOptimistic = (
  fn: (tx: Tx) => void,
  opts: {createdIds?: CreatedIdsFn} = {}
): ImplicitUpdate => ({
  desc: {type: "void", optimistic: (tx) => fn(tx), createdIds: opts.createdIds},
  data: {},
});

export const implVoid = (data: Record<string, any> = {}): ImplicitUpdate => ({
  desc: {type: "void"},
  data,
});

type GetModelFromCache = (modelName: ModelName, id: string) => Record<string, any> | undefined;

// The `implicit` callback runs twice (see `cache.ts`):
// 1. Optimistically, before the server responds — second arg is
//    `{_isOptimistic: true, _getModelFromCache}`. The retVal isn't available yet,
//    so accessing fields off it would crash (this is a real past bug — see
//    `workflowTemplates.clone` accessing `newDeckIds.length`).
// 2. On resolve — second arg is `{...retVal, _getModelFromCache}` with no
//    `_isOptimistic` set.
//
// Modeling this as a discriminated union forces callers to narrow on
// `_isOptimistic` before reading retVal fields, so the optimistic-phase bug
// becomes a type error instead of a runtime crash.
export type ImplicitOpts<TRetVal> =
  | {_isOptimistic: true; _getModelFromCache: GetModelFromCache}
  | ((TRetVal extends void ? {} : TRetVal) & {
      _isOptimistic?: false | undefined;
      _getModelFromCache: GetModelFromCache;
    });

/**
 * `TSupport` holds fields only the cache reads; they are never sent. A push from another client
 * replays `convertDataForOptimistic` and `implicit` with the server's params alone, so those see
 * them as optional.
 */
export type MutationDef<TParams = any, TRetVal = any, TSupport = {}> = {
  convertDataForOptimistic?: (data: TParams & Partial<TSupport>) => Record<string, any>;
  implicit?: (data: TParams & Partial<TSupport>, opts: ImplicitOpts<TRetVal>) => ImplicitUpdate[];
  /**
   * Shows the result before the server answers; replaces the one an `update`/`create`/`delete`
   * gets by default. Receives the data after `convertDataForOptimistic`.
   */
  optimistic?: (tx: Tx, data: TParams & TSupport) => void;
  createdIds?: (retVal: TRetVal) => Partial<Record<ModelName, string | Record<string, string>>>;
} & ({type: "create" | "update" | "delete"; model: ModelName} | {type: "void"});

// Phantom brands so `Mutate<typeof mutations>` can recover per-entry params/retVal/support.
// They exist only in the type system — `defineMutation` returns the input unchanged.
declare const paramsBrand: unique symbol;
declare const retValBrand: unique symbol;
declare const supportBrand: unique symbol;

type Typed<TParams, TRetVal, TSupport> = {
  readonly [paramsBrand]: TParams;
  readonly [retValBrand]: TRetVal;
  readonly [supportBrand]: TSupport;
};

export const defineMutation = <TParams = any, TRetVal = any, TSupport = {}>(
  def: MutationDef<TParams, TRetVal, TSupport>
) => def as MutationDef<TParams, TRetVal, TSupport> & Typed<TParams, TRetVal, TSupport>;

type ParamsOf<T> = T extends Typed<infer P, any, any> ? P : any;
type RetValOf<T> = T extends Typed<any, infer R, any> ? R : any;
type SupportOf<T> = T extends Typed<any, any, infer S> ? S : any;

/** The second argument can be left out when none of its fields is required. */
type MutateFn<P, R, S> = {} extends S
  ? (data?: P, support?: S) => Promise<R>
  : (data: P, support: S) => Promise<R>;

type NsOf<K> = K extends `${infer Ns}.${string}` ? Ns : never;
type ActionOf<K, Ns extends string> = K extends `${Ns}.${infer A}` ? A : never;

// Derives the shape of `api.mutate` from the set of registered mutation keys.
// Entries wrapped with `defineMutation<Params, RetVal, Support>(...)` carry their types
// through; plain `MutationDef<any, any>` entries fall back to `any`.
export type Mutate<M extends Record<string, unknown>> = {
  [Ns in NsOf<keyof M & string>]: {
    [A in ActionOf<keyof M & string, Ns>]: MutateFn<
      ParamsOf<M[`${Ns}.${A}` & keyof M]>,
      RetValOf<M[`${Ns}.${A}` & keyof M]>,
      SupportOf<M[`${Ns}.${A}` & keyof M]>
    >;
  };
};
